<template>
  <div class="total-reflect-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$θ_c = arcsin(n2 / n1)$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>入射角 θ₁</label>
            <span class="val">{{ theta1.toFixed(1) }}°</span>
          </div>
          <div class="hud-item orange">
            <label>临界角 θ_c</label>
            <span class="val">{{ criticalAngle.toFixed(1) }}°</span>
          </div>
          <div class="hud-item" :class="isTotalReflect ? 'green' : 'gray'">
             <label>状态</label>
             <span class="val">{{ isTotalReflect ? '全反射模式' : '折射模式' }}</span>
          </div>
        </div>

        <svg width="100%" height="240" viewBox="0 0 450 240" preserveAspectRatio="xMidYMid meet">
          <defs>
             <filter id="glow-beam"><feGaussianBlur stdDeviation="2.5" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
          </defs>

          <!-- 疏/密介质分界线 -->
          <rect x="0" y="120" width="450" height="120" fill="#0f172a" opacity="0.8" />
          <line x1="0" y1="120" x2="450" y2="120" stroke="#334155" stroke-width="2" />
          <text x="20" y="110" fill="#64748b" font-size="10">n₂ = 1.0 (空气 - 疏媒质)</text>
          <text x="20" y="140" fill="#94a3b8" font-size="10">n₁ = {{ n1.toFixed(2) }} (水/玻璃 - 密媒质)</text>

          <!-- 法线 -->
          <line x1="225" y1="40" x2="225" y2="200" stroke="#475569" stroke-dasharray="4,4" />

          <!-- A. 入射光 (Incident Beam) -->
          <line :x1="beamIn.x1" :y1="beamIn.y1" x2="225" y2="120" stroke="#fbbf24" stroke-width="4" filter="url(#glow-beam)" />
          
          <!-- B. 折射光 (Refracted Beam) - 当 θ < θ_c -->
          <line v-if="!isTotalReflect" x1="225" y1="120" :x2="beamOut.x2" :y2="beamOut.y2" stroke="#0ea5e9" stroke-width="3" opacity="0.6" filter="url(#glow-beam)" />
          
          <!-- C. 反射光 (Reflected Beam) -->
          <line x1="225" y1="120" :x2="beamReflect.x2" :y2="beamReflect.y2" stroke="#fbbf24" :stroke-width="isTotalReflect ? 4 : 1.5" :opacity="isTotalReflect ? 0.8 : 0.3" filter="url(#glow-beam)" />

          <!-- 临界角刻度扇形 -->
          <path :d="angleArc" fill="rgba(251, 191, 36, 0.1)" stroke="none" />
        </svg>

        <div class="physics-caption">
           现象：当入射角大于临界角时，折射光彻底消失，能量全部反射回原介质。
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex-row items-center gap-6">
              <div class="flex-1">
                 <label class="s-label">调节入射角 (θ₁)：{{ theta1.toFixed(1) }}°</label>
                 <el-slider v-model="theta1" :min="0" :max="85" size="small"></el-slider>
              </div>
              <div class="flex-1">
                 <label class="s-label">调节折射率 n₁ (介质种类)</label>
                 <el-slider v-model="n1" :min="1.3" :max="2.4" :step="0.01" size="small"></el-slider>
              </div>
           </div>
        </div>

        <!-- 针对性文字对齐：深蓝高对比度卡片，保证阅读清晰 -->
        <div class="discovery-card-discovery-tir" v-latex>
           <div class="icon-wrap"><i class="el-icon-position"></i></div>
           <div class="text-content">
             <p><strong>实战物理/光纤连接器：</strong>全反射是光信号飞驰万里的核心。<strong>实验规律：</strong>
                1. 调节 $n_1$ 滑块：你会看到临界角 $θ_c$ 随折射率增大而减小；
                2. 观察能量变化：一旦超过 $θ_c$，由于没有能量损失，光可以在弯曲的玻璃纤维中不断弹跳前进。
                这就是为什么我们可以通过一根头发丝粗细的光纤，传输整栋楼的海量数据。
             </p>
           </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'TotalInternalReflectionViz',
  data() {
    return {
      theta1: 30,
      n1: 1.5
    }
  },
  computed: {
    criticalAngle() {
       return Math.asin(1.0 / this.n1) * 180 / Math.PI;
    },
    isTotalReflect() {
       return this.theta1 >= this.criticalAngle;
    },
    theta1Rad() { return (this.theta1 * Math.PI) / 180; },
    beamIn() {
       return { x1: 225 - Math.sin(this.theta1Rad)*100, y1: 120 + Math.cos(this.theta1Rad)*100 };
    },
    beamOut() {
       // n1 sin t1 = n2 sin t2 => sin t2 = n1 sin t1 / 1
       const sinT2 = this.n1 * Math.sin(this.theta1Rad);
       if (sinT2 > 1) return { x2: 225, y2: 120 };
       const t2 = Math.asin(sinT2);
       return { x2: 225 + Math.sin(t2)*120, y2: 120 - Math.cos(t2)*120 };
    },
    beamReflect() {
       return { x2: 225 + Math.sin(this.theta1Rad)*100, y2: 120 + Math.cos(this.theta1Rad)*100 };
    },
    angleArc() {
       const r = 40;
       const x = 225 - Math.sin(this.theta1Rad)*r;
       const y = 120 + Math.cos(this.theta1Rad)*r;
       return `M 225,120 L 225,${120+r} A ${r},${r} 0 0,1 ${x},${y} Z`;
    }
  }
}
</script>

<style scoped lang="scss">
.total-reflect-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #eff6ff; color: #1e40af; border: 1px solid #bfdbfe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 240px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #334155; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #f8fafc; font-weight: 800; font-family: monospace; } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #fbbf24; } &.green { border-left: 3px solid #10b981; } &.gray { border-left: 3px solid #64748b; opacity: 0.6; } } }
.physics-caption { position: absolute; bottom: 8px; width: 100%; text-align: center; color: #334155; font-size: 11px; font-weight: bold; }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; }
.s-label { font-size: 11px; color: #64748b; font-weight: 800; margin-bottom: 2px; display: block; }
.flex-row { display: flex; flex-direction: row; } .items-center { align-items: center; } .gap-6 { gap: 24px; } .flex-1 { flex: 1; }

/* 针对性加固：深蓝高对比度卡片，杜绝文字模糊 */
.discovery-card-discovery-tir { 
  background: #eff6ff; 
  padding: 12px 16px; 
  border-radius: 10px; 
  border: 1px solid #bfdbfe; 
  display: flex; gap: 14px; 
  align-items: center;
  i { color: #1e40af; font-size: 1.4rem; }
  p { 
    margin: 0; 
    font-size: 11.5px; 
    color: #1e3a8a; // 深蓝色文字，极高对比度
    line-height: 1.6;
    strong { color: #1e3a8a; font-weight: 900; }
  }
}
</style>
